/**
 * Luật thuần của màn Cấu hình chatbot — port các hàm trong `components/cau-hinh-bot/*` và `components/KichBan.tsx` của
 * native. Không đụng React, không dựng chuỗi hiển thị: trả mã để component tự dịch.
 */
import type {
  BotGrant,
  BuocHoiThoai,
  ChatbotInput,
  KhuonGrant,
  KieuTruong,
  PermGrant,
  Scenario,
  TruongBatBuoc,
} from '../types';

/*
 * Bản sao của `docBuoc`/`docTruong` ở `types.ts`: tệp kia nhập `styles/tokens` nên `node --test` không nạp được nó lúc
 * chạy. Hai hàm một dòng, cùng nghĩa — đọc bản khai cũ dạng chuỗi về dạng object.
 */
const docBuoc = (b: string | BuocHoiThoai): BuocHoiThoai =>
  typeof b === 'string' ? { ten: b, thuThap: false, kieu: 'van_ban' } : b;
const docTruong = (t: string | TruongBatBuoc): TruongBatBuoc =>
  typeof t === 'string' ? { ten: t, kieu: 'van_ban' } : t;

/** Mã màu khung nhúng máy chủ nhận: `#RGB` hoặc `#RRGGBB`. */
export const MAU_HOP_LE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Nở mã màu ba chữ số thành sáu chữ số; chuỗi không đọc được → `macDinh`. `<input type="color">` chỉ nhận đúng dạng sáu
 * chữ số — đưa gì khác vào thì trình duyệt lặng lẽ đổi thành đen trong khi ô chữ vẫn hiện chuỗi đang gõ dở.
 */
export function mauSauChuSo(mau: string, macDinh: string): string {
  const khop = MAU_HOP_LE.exec(mau.trim());
  const v = (khop?.[0] ?? macDinh).slice(1).toLowerCase();
  return v.length === 3 ? `#${[...v].map((c) => c + c).join('')}` : `#${v}`;
}

/**
 * Ô "mỗi dòng một mục" (tên miền, gốc nhúng): tách đoạn đang gõ thành danh sách. Ô giữ NGUYÊN VĂN khi đang gõ và chỉ báo
 * lên danh sách đã tách — dựng lại ô từ danh sách đã lọc sau mỗi phím là nuốt mất dấu xuống dòng dưới tay người gõ.
 */
export function tachDong(van: string): string[] {
  return van
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean);
}

/** `*` mở cho mọi gốc — cùng phép so `.trim() === '*'` phía máy chủ, không khớp chuỗi con. */
export function moMoiGoc(goc: readonly string[]): boolean {
  return goc.some((x) => x.trim() === '*');
}

export const TABS_BOT = ['tongQuan', 'triThuc', 'hanhVi', 'giaoTiep', 'quyen', 'nhung', 'nangCao'] as const;
export type TabBot = (typeof TABS_BOT)[number];

/**
 * Trường nào thuộc tab nào — để chấm "chưa lưu" rơi đúng tab. `active` không thuộc tab nào: công tắc bật ở phần đầu,
 * nhìn thấy từ mọi tab. Tab Quyền chỉ mang `phamVi` (khứ hồi, không có ô chọn ở platform); ô tích vai lưu riêng.
 */
const TRUONG_CUA_TAB: Record<TabBot, (f: ChatbotInput) => unknown> = {
  tongQuan: (f) => [f.name, f.shortDescription, f.purpose, f.widget.greeting],
  triThuc: (f) => [f.mode, f.trichNguon, f.knowledgeBaseIds, f.sourceIds, f.webDomains],
  hanhVi: (f) => [f.systemPrompt, f.faqs, f.scenariosEnabled, f.scenarios],
  giaoTiep: (f) => [f.cheDoGiaoTiep, f.giong],
  quyen: (f) => [f.phamVi],
  nhung: (f) => ({ ...f.widget, greeting: undefined }),
  nangCao: (f) => [f.saveHistory],
};

export const giongNhau = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Tab có thay đổi chưa lưu so với bản gốc. */
export function tabChuaLuu(f: ChatbotInput, goc: ChatbotInput): TabBot[] {
  return TABS_BOT.filter((k) => !giongNhau(TRUONG_CUA_TAB[k](f), TRUONG_CUA_TAB[k](goc)));
}

/** Tab đang có lỗi cần sửa trước khi lưu/bật. */
export function loiCuaTab(f: ChatbotInput): Partial<Record<TabBot, boolean>> {
  return {
    tongQuan: !f.name.trim(),
    triThuc: !f.knowledgeBaseIds.length && !f.sourceIds.length,
    nhung: !!f.widget.primaryColor && !MAU_HOP_LE.test(f.widget.primaryColor.trim()),
  };
}

/** Việc còn thiếu trước khi bật được bot, theo thứ tự hiện. */
export function viecConThieu(f: ChatbotInput): ('datTen' | 'ganNguon')[] {
  const ra: ('datTen' | 'ganNguon')[] = [];
  if (!f.name.trim()) ra.push('datTen');
  if (f.knowledgeBaseIds.length + f.sourceIds.length === 0) ra.push('ganNguon');
  return ra;
}

/** Trạng thái chấm của một dòng bot: tắt VÀ không trả lời được mới đáng cảnh báo. */
export function trangThaiDongBot(b: { active: boolean; answerable: boolean }): 'chay' | 'tu_tat' | 'tat' {
  if (b.active && b.answerable) return 'chay';
  if (!b.active && !b.answerable) return 'tu_tat';
  return 'tat';
}

// ── Quyền dùng bot ────────────────────────────────────────────────────────────

/** Vai này có thấy bot này không — cùng luật `QuyenChatbot.thay_duoc` ở máy chủ: `chan` thắng cả `full`. */
export function thayBot(g: BotGrant, botId: string): boolean {
  if (g.level === 'none' || g.chan.includes(botId)) return false;
  return g.level === 'full' || g.chatbot.includes(botId);
}

/**
 * Bật / tắt MỘT bot cho MỘT vai, giữ nguyên mọi bot khác của vai ấy. Không bao giờ ghi `chan` (màn vai trò cũng không);
 * bỏ tích một bot ở vai `full` thì chuyển sang `chon` với mọi bot CÒN LẠI — "bot tạo sau tự thấy" không còn đúng nữa.
 */
export function doiQuyenBot(g: BotGrant, botId: string, bat: boolean, moiBot: string[]): BotGrant {
  const dangThay = new Set(moiBot.filter((id) => thayBot(g, id)));
  if (bat) dangThay.add(botId);
  else dangThay.delete(botId);
  const ds = moiBot.filter((id) => dangThay.has(id));
  if (ds.length === 0) return { role: g.role, level: 'none', chatbot: [], chan: [] };
  if (ds.length === moiBot.length) return { role: g.role, level: 'full', chatbot: [], chan: [] };
  return { role: g.role, level: 'chon', chatbot: ds, chan: [] };
}

/**
 * Dòng quyền bot cho mọi vai của tổ chức: dòng máy chủ đã có giữ nguyên, vai tổ chức chưa có dòng thì thêm dòng đóng
 * (`none`) — ma trận mặc định đóng, nên dòng thêm vào không đổi quyền ai cho tới khi có người tích.
 */
export function gopDongQuyenBot(grants: BotGrant[], vai: readonly string[]): BotGrant[] {
  const co = new Set(grants.map((g) => g.role));
  return [...grants, ...vai.filter((r) => !co.has(r)).map((role) => ({ role, level: 'none' as const, chatbot: [], chan: [] }))];
}

/** Bỏ dòng đóng không có trong bản gốc máy chủ — để "có thay đổi" không bật chỉ vì vừa gộp vai vào. */
export function boDongDongThem<T extends { role: string; level: string }>(ds: T[], goc: readonly { role: string }[]): T[] {
  const coGoc = new Set(goc.map((g) => g.role));
  return ds.filter((g) => coGoc.has(g.role) || g.level !== 'none');
}

// ── Vai của khung nhúng ───────────────────────────────────────────────────────

/** Mức đọc của vai nhúng trên một nguồn của bot. */
export type MucVaiNhung = 'khuon' | 'khuon_none' | 'full' | 'tables' | 'none';

export interface DongVaiNhung {
  id: string;
  muc: MucVaiNhung;
  soBang: number;
}

/**
 * Vai nhúng đọc được gì trên TỪNG nguồn của bot. Quyền khoá theo cặp (vai, nguồn); nguồn trả lời theo khuôn đọc ma trận
 * khuôn, không đọc ma trận bảng (ma trận bảng của nó đang ngủ). Đọc `level` trước, đừng đếm `tables`: mức `full` có
 * `tables` RỖNG theo thiết kế.
 */
export function danhGiaVaiNhung(
  vai: string,
  sourceIds: readonly string[],
  theoKhuon: ReadonlySet<string>,
  quyenBang: readonly (PermGrant & { sourceId: string })[],
  maTranKhuon: Readonly<Record<string, readonly KhuonGrant[] | undefined>>,
): DongVaiNhung[] {
  return sourceIds.map((id) => {
    if (theoKhuon.has(id)) {
      const g = maTranKhuon[id]?.find((x) => x.role === vai);
      const co = !!g && (g.level === 'full' || (g.level === 'chon' && g.nhom.length + g.khuon.length > 0));
      return { id, muc: co ? 'khuon' : 'khuon_none', soBang: 0 };
    }
    const g = quyenBang.find((x) => x.sourceId === id);
    return { id, muc: g?.level ?? 'none', soBang: g?.tables.length ?? 0 };
  });
}

/** Nguồn có vấn đề (không đọc được gì, hoặc mở toàn bộ cho khách vãng lai). */
export function laCoVanDe(d: DongVaiNhung): boolean {
  return d.muc === 'none' || d.muc === 'full' || d.muc === 'khuon_none';
}

/** Số nguồn vai nhúng đọc được số liệu. */
export function soNguonDocDuoc(ds: readonly DongVaiNhung[]): number {
  return ds.filter((d) => d.muc !== 'none' && d.muc !== 'khuon_none').length;
}

// ── Kịch bản ──────────────────────────────────────────────────────────────────

export const KICH_BAN_RONG: Scenario = {
  name: '',
  trigger: '',
  reply: '',
  requiredFields: [],
  steps: [],
  rules: [],
};

export const KIEU_TRUONG: KieuTruong[] = ['van_ban', 'so', 'dien_thoai', 'email', 'ngay'];

/** Bỏ dấu, bỏ dấu câu, gộp khoảng trắng — để so hai chuỗi người gõ. */
export function chuanHoaChu(s: string): string {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Gộp bản khai CŨ (hai danh sách `requiredFields` + `steps`) thành một danh sách bước. Trường bắt buộc gán vào bước NÓI
 * VỀ NÓ — khớp theo TỪ TRỌN VẸN, không theo chuỗi con (trường "B" không được cướp bước "b1"), cùng luật `viec()` bên trợ
 * lý. Trường không khớp bước nào thành bước mới ở cuối — thấy được và sửa được, thay vì biến mất.
 */
export function gopBanKhaiCu(k: Scenario): BuocHoiThoai[] {
  const buoc = (k.steps ?? []).map(docBuoc);
  if (buoc.some((b) => b.thuThap)) return buoc;
  const con: BuocHoiThoai[] = [];
  for (const truong of (k.requiredFields ?? []).map(docTruong)) {
    const khoa = chuanHoaChu(truong.ten);
    const i = buoc.findIndex(
      (b) => !b.thuThap && khoa.length >= 3 && ` ${chuanHoaChu(b.ten)} `.includes(` ${khoa} `),
    );
    const cu = i >= 0 ? buoc[i] : undefined;
    if (cu) buoc[i] = { ...cu, thuThap: true, kieu: truong.kieu };
    else con.push({ ten: truong.ten, thuThap: true, kieu: truong.kieu });
  }
  return [...buoc, ...con];
}

/** Bao nhiêu bước phải thu thập — cho dòng tóm tắt ở danh sách kịch bản. */
export function soThuThap(k: Scenario): number {
  return gopBanKhaiCu(k).filter((b) => b.thuThap).length;
}

/**
 * Chuẩn hoá kịch bản trước khi lưu. `requiredFields` về rỗng vì mọi lời khai đã nằm trong `steps` — giữ cả hai là để bản
 * chạy cộng dồn hai lời khai cho một ô và hỏi nó hai lần. Bỏ mục rỗng; kiểu của bước không thu thập về `van_ban`.
 */
export function chuanHoaKichBan(f: Scenario): Scenario {
  return {
    ...f,
    name: f.name.trim(),
    trigger: f.trigger.trim(),
    requiredFields: [],
    steps: f.steps
      .map(docBuoc)
      .map((x) => ({ ...x, ten: x.ten.trim() }))
      .filter((x) => x.ten)
      .map((x) => (x.thuThap ? x : { ...x, kieu: 'van_ban' as KieuTruong })),
    rules: f.rules.map((x) => x.trim()).filter(Boolean),
  };
}

/** Đổi chỗ hai phần tử; chỉ mục ngoài khoảng thì trả lại nguyên mảng. */
export function doiCho<T>(ds: readonly T[], i: number, j: number): T[] {
  const d = [...ds];
  const a = d[i];
  const b = d[j];
  if (a === undefined || b === undefined) return d;
  d[i] = b;
  d[j] = a;
  return d;
}
